import React, { useState } from 'react';
import { Container, Title, Stack, Button, Group, Divider } from '@mantine/core';
import {
  MentionList,
  MentionDetailModal,
  BusinessMatchInfo,
  RegionCard,
  SubredditTagInput,
  CoordinateInput,
  AccessDeniedMessage,
} from '../components/domain';
import type { RedditMentionDto } from '../../domain/entities/RedditMention';
import type { BusinessDto } from '../../domain/entities/Business';
import type { RegionDto } from '../../domain/entities/Region';
import type { Coordinates } from '../../domain/value-objects/Coordinates';
import { createMockRedditMentionDto } from '../../__tests__/fixtures';

/**
 * Component Test Page
 *
 * This page is used to visually test all new components from Sprint 005.
 * It provides sample data and interactive controls for each component.
 */
export const ComponentTestPage: React.FC = (): React.ReactElement => {
  // Sample data
  const mockMention: RedditMentionDto = createMockRedditMentionDto({
    id: '123e4567-e89b-12d3-a456-426614174000',
    redditPostId: 'abc123',
    postTitle: 'Looking for a great coffee shop in downtown Seattle',
    authorUsername: 'reddit_user_123',
    postPermalink: '/r/seattle/comments/abc123',
    subreddit: 'seattle',
    content: 'I love the espresso at Pike Place Market!',
    matchConfidenceScore: 85,
    emotionalTone: 'Positive',
    emotionalToneScore: 0.85,
    businessId: '456e7890-e89b-12d3-a456-426614174001',
    businessName: 'Pike Place Coffee',
    matchStatus: 'Unmatched',
    processingStatus: 'Processed',
    upvoteCount: 42,
    commentCount: 5,
    isComment: false,
    postCreatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  });

  const mockBusiness: BusinessDto = {
    id: '456e7890-e89b-12d3-a456-426614174001',
    name: 'Pike Place Coffee',
    category: 'Coffee Shop',
    businessType: 'cafe',
    description: 'Local coffee shop serving artisanal espresso',
    regionId: '789e0123-e89b-12d3-a456-426614174002',
    phone: '(206) 555-1234',
    email: 'info@pikeplacecoffee.com',
    website: 'https://pikeplacecoffee.com',
    openingHours: 'Mon-Fri: 7am-8pm, Sat-Sun: 8am-9pm',
    addressLine1: '123 Pike Place',
    addressLine2: null,
    isUserCreated: false,
    isActive: true,
  };

  const mockRegion: RegionDto = {
    id: '789e0123-e89b-12d3-a456-426614174002',
    regionId: 'seattle-downtown',
    regionName: 'Seattle Downtown',
    country: 'United States',
    state: 'Washington',
    city: 'Seattle',
    subAreas: [],
    subreddits: ['seattle', 'SeattleWA', 'SeattleFood'],
    description: 'Downtown Seattle region for local business monitoring',
    isActive: true,
  };

  // State for interactive components
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [subreddits, setSubreddits] = useState<string[]>(['seattle', 'SeattleWA']);
  const [coordinates, setCoordinates] = useState<Coordinates>({
    latitude: 47.6062,
    longitude: -122.3321,
  });
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  const handleConfirm = async (id: string): Promise<void> => {
    console.log('Confirm mention:', id);
  };

  const handleDismiss = async (id: string): Promise<void> => {
    console.log('Dismiss mention:', id);
  };

  const handleMentionClick = (mention: RedditMentionDto): void => {
    console.log('Mention clicked:', mention);
    setDetailModalOpen(true);
  };

  return (
    <Container size="xl" py="xl">
      <Title order={1} mb="xl">Sprint 005 - Component Test Page</Title>

      {/* MentionList Component */}
      <Stack gap="xl">
        <div>
          <Title order={2} mb="md">1. MentionList Component</Title>
          <MentionList
            mentions={[mockMention]}
            onMentionClick={handleMentionClick}
            onConfirm={handleConfirm}
            onDismiss={handleDismiss}
            currentPage={1}
            totalPages={1}
          />
        </div>

        <Divider />

        {/* MentionDetailModal Component */}
        <div>
          <Title order={2} mb="md">2. MentionDetailModal Component</Title>
          <Group>
            <Button onClick={(): void => setDetailModalOpen(true)}>
              Open Detail Modal
            </Button>
          </Group>
          <MentionDetailModal
            mention={mockMention}
            opened={detailModalOpen}
            onClose={(): void => setDetailModalOpen(false)}
          />
        </div>

        <Divider />

        {/* BusinessMatchInfo Component */}
        <div>
          <Title order={2} mb="md">3. BusinessMatchInfo Component</Title>
          <Stack gap="md">
            <div>
              <Title order={4} size="sm" mb="xs">Compact Mode:</Title>
              <BusinessMatchInfo business={mockBusiness} compact />
            </div>
            <div>
              <Title order={4} size="sm" mb="xs">Detailed Mode:</Title>
              <BusinessMatchInfo business={mockBusiness} detailed />
            </div>
            <div>
              <Title order={4} size="sm" mb="xs">No Business (null):</Title>
              <BusinessMatchInfo business={null} />
            </div>
          </Stack>
        </div>

        <Divider />

        {/* RegionCard Component */}
        <div>
          <Title order={2} mb="md">4. RegionCard Component</Title>
          <Stack gap="md">
            <div>
              <Title order={4} size="sm" mb="xs">Compact Mode:</Title>
              <RegionCard region={mockRegion} />
            </div>
            <div>
              <Title order={4} size="sm" mb="xs">Detailed Mode with Actions:</Title>
              <RegionCard
                region={mockRegion}
                detailed
                showActions
                onEdit={(id): void => console.log('Edit region:', id)}
                onDelete={(id): void => console.log('Delete region:', id)}
              />
            </div>
          </Stack>
        </div>

        <Divider />

        {/* SubredditTagInput Component */}
        <div>
          <Title order={2} mb="md">5. SubredditTagInput Component</Title>
          <SubredditTagInput
            value={subreddits}
            onChange={setSubreddits}
            label="Subreddits"
            description="Add subreddits to monitor (with or without r/ prefix)"
            placeholder="Enter subreddit name"
          />
          <div style={{ marginTop: '1rem' }}>
            Current value: {JSON.stringify(subreddits)}
          </div>
        </div>

        <Divider />

        {/* CoordinateInput Component */}
        <div>
          <Title order={2} mb="md">6. CoordinateInput Component</Title>
          <CoordinateInput
            value={coordinates}
            onChange={setCoordinates}
            label="Region Center Coordinates"
            description="Enter the latitude and longitude for the region center"
          />
          <div style={{ marginTop: '1rem' }}>
            Current value: {JSON.stringify(coordinates)}
          </div>
        </div>

        <Divider />

        {/* AccessDeniedMessage Component */}
        <div>
          <Title order={2} mb="md">7. AccessDeniedMessage Component</Title>
          <Group mb="md">
            <Button onClick={(): void => setShowAccessDenied(!showAccessDenied)}>
              {showAccessDenied ? 'Hide' : 'Show'} Access Denied Message
            </Button>
          </Group>
          {showAccessDenied && (
            <AccessDeniedMessage
              resource="Admin Dashboard"
              requiredRole="Admin"
              showBackButton
              showLoginButton
              onBack={(): void => console.log('Back clicked')}
              onLogin={(): void => console.log('Login clicked')}
            />
          )}
        </div>
      </Stack>
    </Container>
  );
};
